import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import moment from 'moment';

const ISO_8601_FORMAT = 'YYYY-MM-DD';

/**
 * Convert ISO 8601 date into moment.js object
 * @param {string} dateString - ISO 8601 date
 */
export const toMoment = (dateString) => moment(dateString, moment.ISO_8601);

export const getToday = () => moment().startOf('day');

export const isBetween = (testDate, range, inclusive = true) =>
  moment(toMoment(testDate)).isBetween(
    range[0].format(ISO_8601_FORMAT),
    range[range.length - 1].format(ISO_8601_FORMAT),
    null,
    inclusive ? '[]' : '()',
  );

export const isBefore = (testDate, date) =>
  moment(toMoment(testDate)).isBefore(toMoment(date).format(ISO_8601_FORMAT));

export const fillMissingDates = (arr, range) => {
  if (arr.length < 1) {
    const diff = range[1].diff(range[0], 'days');
    const toReturn = [];
    for (let i = 0; i < diff; i++) {
      toReturn.push({});
    }
    return toReturn;
  } else {
    const toReturn = [];
    let diff = moment(arr[0].createdDate).diff(range[0], 'days');
    for (let i = 0; i < diff; i++) {
      toReturn.push({});
    }
    for (let i = 0; i < arr.length - 1; i++) {
      toReturn.push(arr[i]);
      const diff = moment(arr[i + 1].createdDate).diff(
        moment(arr[i].createdDate),
        'days',
      );
      for (let j = 0; j < diff; j++) {
        toReturn.push({});
      }
    }
    toReturn.push(arr[arr.length - 1]);
    diff = range[1].diff(moment(arr[arr.length - 1].createdDate), 'days');
    for (let i = 0; i < diff; i++) {
      toReturn.push({});
    }
    return toReturn;
  }
};

export const getFileTimeStamp = () => {
  const date = new Date();

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const formatTimestamp = (ts) => {
  return moment(ts).calendar(null, {
    sameDay: '[Today], h:mm A',
    lastDay: '[Yesterday], h:mm A',
    lastWeek: 'MMMM DD, h:mm A',
    sameElse: 'MMMM DD, h:mm A',
  });
};

export const formatTimestampGroup = (ts) => {
  return moment(ts).calendar(null, {
    sameDay: '[Today]',
    lastDay: '[Yesterday]',
    lastWeek: '[Last Week]',
    sameElse: 'MMMM YYYY',
  });
};

export const groupItemsByTimestamp = (items) => {
  const groups = {};
  items.forEach((item) => {
    const datetime = formatTimestampGroup(item.created);
    if (!groups[datetime]) {
      groups[datetime] = [];
    }
    groups[datetime].push(item);
  });
  return groups;
};

export const utcToDateWithTimeZone = (date) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneAbbr = zonedTimeToUtc(new Date(), timeZone)
    .toLocaleTimeString('en-US', { timeZoneName: 'short' })
    .split(' ')[2];
  const localDate = utcToZonedTime(date, timeZone);
  const formattedDate = format(localDate, 'MMMM d');
  const formattedTime = format(localDate, 'hh:mm a');

  return `${formattedDate} at ${formattedTime} ${timeZoneAbbr}`;
};

/**
 * Formats a UTC date string to local time with a custom format
 * @param {string} utcDateString - UTC date string (ISO 8601)
 * @param {string} formatString - Moment.js format string (e.g., 'MM/DD/YYYY h:mm A')
 * @returns {string} Formatted date string in local timezone
 */
export const formatUtcToLocal = (utcDateString, formatString) => {
  return moment.utc(utcDateString).local().format(formatString);
};
